import { Alert, Box, Divider } from "@mui/material";
import React, { useEffect, useState } from "react";
import LogoLoader from "../common/LogoLoader";
import { EdgeDetail, graphService } from "../../services/graphService";
import EdgeDetailComponent from "./EdgeDetailOverlay/EdgeDetail";
import EdgeHeader from "./EdgeDetailOverlay/EdgeHeader";
import EdgeRelationshipTabs from "./EdgeDetailOverlay/EdgeRelationshipTabs";

interface EdgeDetailOverlayProps {
  relationshipId: string;
  onClose: () => void;
  onEntityClick?: (entityId: string) => void;
}

const EdgeDetailOverlay: React.FC<EdgeDetailOverlayProps> = ({
  relationshipId,
  onClose,
  onEntityClick,
}) => {
  const [edgeDetailResponse, setEdgeDetailResponse] =
    useState<EdgeDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch edge detail data when relationshipId changes
  useEffect(() => {
    const fetchEdgeDetail = async () => {
      if (!relationshipId) {
        setEdgeDetailResponse(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const edgeData = await graphService.getEdgeDetail(relationshipId);

        if (edgeData) {
          setEdgeDetailResponse(edgeData);
        } else {
          setError("No data found for this relationship");
        }
      } catch (err: any) {
        setError(err.message || "Failed to load relationship details");
      } finally {
        setLoading(false);
      }
    };

    fetchEdgeDetail();
  }, [relationshipId]);

  if (!relationshipId) return null;

  return (
    <Box
      data-overlay-content
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        width: "45%",
        height: "100%",
        backgroundColor: "background.paper",
        borderLeft: 1,
        borderColor: "divider",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <LogoLoader size="panel" surface="paper" label="Loading the relation" />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">{error}</Alert>
        </Box>
      ) : edgeDetailResponse ? (
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EdgeHeader edgeDetail={edgeDetailResponse} onClose={onClose} />

          {/* Section 1: Edge Overview - Two-column entity display */}
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <EdgeDetailComponent
              edgeDetail={edgeDetailResponse}
              onEntityClick={onEntityClick}
            />
          </Box>

          <Divider />

          {/* Section 2: Relationship Sources Tab */}
          <EdgeRelationshipTabs
            edgeDetail={edgeDetailResponse}
            onEntityClick={onEntityClick}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default EdgeDetailOverlay;
